"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { BookOpen, ArrowRight } from "lucide-react";
import type { TopicLink } from "@/lib/server-utils/topic-links";

const STORAGE_KEY = "learningPathNav";

function getStoredState(type: string): boolean | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Record<string, boolean>;
    return parsed[type] ?? null;
  } catch {
    return null;
  }
}

function setStoredState(type: string, open: boolean) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? (JSON.parse(stored) as Record<string, boolean>) : {};
    parsed[type] = open;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // localStorage 접근 불가 시 무시
  }
}

interface LearningPathNavProps {
  items: TopicLink[];
  type: "prerequisites" | "nextTopics";
  defaultOpen?: boolean;
}

export default function LearningPathNav({ items, type, defaultOpen = false }: LearningPathNavProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  // localStorage 외부 동기화 — JCG-2034에서 마이그레이션 예정
  useEffect(() => {
    const stored = getStoredState(type);
    if (stored !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(stored);
    }
  }, [type]);

  const handleToggle = useCallback(() => {
    const nowOpen = detailsRef.current?.open ?? false;
    setIsOpen(nowOpen);
    setStoredState(type, nowOpen);
  }, [type]);

  if (items.length === 0) return null;

  const isPrerequisites = type === "prerequisites";
  const label = isPrerequisites ? "선수학습" : "후수학습";
  const Icon = isPrerequisites ? BookOpen : ArrowRight;

  return (
    <details
      ref={detailsRef}
      open={isOpen || undefined}
      onToggle={handleToggle}
      className={`border rounded-lg my-4 overflow-hidden ${
        isPrerequisites
          ? "border-blue-200 dark:border-blue-800"
          : "border-green-200 dark:border-green-800"
      }`}
    >
      <summary
        className={`flex items-center gap-2 px-4 py-3 cursor-pointer font-semibold text-sm select-none ${
          isPrerequisites
            ? "bg-blue-50 hover:bg-blue-100 text-blue-800 dark:bg-blue-950 dark:hover:bg-blue-900 dark:text-blue-200"
            : "bg-green-50 hover:bg-green-100 text-green-800 dark:bg-green-950 dark:hover:bg-green-900 dark:text-green-200"
        }`}
      >
        <Icon className="size-4 shrink-0" />
        <span>
          {label}
          <span className="font-normal text-xs ml-1 opacity-70">
            ({items.length}개)
          </span>
        </span>
      </summary>
      <ul className="px-4 py-3 space-y-2">
        {items.map((item) => (
          <li key={item.url} className="flex items-center gap-2">
            <span
              className={`size-1.5 rounded-full shrink-0 ${
                isPrerequisites
                  ? "bg-blue-400 dark:bg-blue-500"
                  : "bg-green-400 dark:bg-green-500"
              }`}
            />
            <Link
              href={item.url}
              className={`text-sm hover:underline ${
                isPrerequisites
                  ? "text-blue-700 dark:text-blue-300"
                  : "text-green-700 dark:text-green-300"
              }`}
            >
              {item.title}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}
